/**
 * Auth Store — manages user identity, teams, active team context, and computed role.
 * Uses Zustand for state management.
 * @module stores/starnexus/authStore
 */

import { create } from 'zustand';
import { Team, UserRole } from '../../types/starnexus/team';
import { apiUrl, apiFetch } from '../../utils/apiClient';

// ============================================================================
// Constants
// ============================================================================

const ACTIVE_TEAM_KEY = 'starnexus_active_team';

/** Super-users bypass role checks — always treated as Owner. */
const SUPER_USERS: string[] = ['yyangzam'];

// ============================================================================
// Store Interface
// ============================================================================

interface AuthState {
  // Identity
  alias: string | null;
  // Teams
  teams: Team[];
  activeTeam: Team | null;
  // Computed role
  role: UserRole | null;
  // Loading/error
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  // Actions
  initialize: () => Promise<void>;
  setActiveTeam: (teamId: string) => void;
  addTeam: (team: Team) => void;
  reloadTeams: () => Promise<void>;
}

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Compute the user's role within a team.
 * - Super-users always get Owner
 * - Owner if alias is in team.owners[]
 * - Member if alias is in team.members[] but not owners[]
 * - null otherwise
 */
export function computeRole(alias: string, team: Team): UserRole | null {
  if (SUPER_USERS.includes(alias)) return 'Owner';
  if (team.owners.includes(alias)) return 'Owner';
  if (team.members.includes(alias)) return 'Member';
  return null;
}

// ============================================================================
// Helpers
// ============================================================================

function restoreActiveTeam(teams: Team[]): Team | null {
  if (teams.length === 0) return null;

  try {
    const savedId = localStorage.getItem(ACTIVE_TEAM_KEY);
    if (savedId) {
      const match = teams.find((t) => t.teamId === savedId);
      if (match) return match;
    }
  } catch {
    // localStorage unavailable — fall through to default
  }

  return teams[0];
}

function persistActiveTeam(teamId: string): void {
  try {
    localStorage.setItem(ACTIVE_TEAM_KEY, teamId);
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// ============================================================================
// Auth Store
// ============================================================================

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Initial state
  alias: null,
  teams: [],
  activeTeam: null,
  role: null,
  status: 'idle',
  error: null,

  // Actions
  initialize: async () => {
    set({ status: 'loading', error: null });

    try {
      const [whoamiRes, teamsRes] = await Promise.all([
        apiFetch(apiUrl('/whoami')),
        apiFetch(apiUrl('/team/teams/mine')),
      ]);

      if (!whoamiRes.ok) {
        throw new Error(`Failed to fetch identity (${whoamiRes.status})`);
      }
      if (!teamsRes.ok) {
        throw new Error(`Failed to fetch teams (${teamsRes.status})`);
      }

      const whoamiData: { alias: string } = await whoamiRes.json();
      const teamsData: { teams: Team[] } = await teamsRes.json();

      const alias = whoamiData.alias;
      const teams = teamsData.teams;
      const activeTeam = restoreActiveTeam(teams);
      const role = alias && activeTeam ? computeRole(alias, activeTeam) : null;

      if (activeTeam) {
        persistActiveTeam(activeTeam.teamId);
      }

      set({
        alias,
        teams,
        activeTeam,
        role,
        status: 'ready',
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initialize';
      set({ status: 'error', error: message });
    }
  },

  setActiveTeam: (teamId: string) => {
    const { teams, alias } = get();
    const team = teams.find((t) => t.teamId === teamId);
    if (!team) return;

    const role = alias ? computeRole(alias, team) : null;
    persistActiveTeam(teamId);
    set({ activeTeam: team, role });
  },

  addTeam: (team: Team) => {
    const { teams, alias } = get();
    const updatedTeams = [...teams, team];
    const role = alias ? computeRole(alias, team) : null;

    persistActiveTeam(team.teamId);
    set({
      teams: updatedTeams,
      activeTeam: team,
      role,
    });
  },

  reloadTeams: async () => {
    try {
      const res = await apiFetch(apiUrl('/team/teams/mine'));
      if (!res.ok) {
        throw new Error(`Failed to reload teams (${res.status})`);
      }

      const data: { teams: Team[] } = await res.json();
      const { alias, activeTeam } = get();
      const teams = data.teams;

      // Check if current active team still exists in the list
      let newActiveTeam: Team | null = null;
      if (activeTeam && teams.find((t) => t.teamId === activeTeam.teamId)) {
        newActiveTeam = teams.find((t) => t.teamId === activeTeam.teamId) ?? null;
      } else if (teams.length > 0) {
        // Stale active team — fallback to first
        newActiveTeam = teams[0];
      }

      const role = alias && newActiveTeam ? computeRole(alias, newActiveTeam) : null;

      if (newActiveTeam) {
        persistActiveTeam(newActiveTeam.teamId);
      } else {
        try {
          localStorage.removeItem(ACTIVE_TEAM_KEY);
        } catch {
          // ignore
        }
      }

      set({
        teams,
        activeTeam: newActiveTeam,
        role,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reload teams';
      set({ status: 'error', error: message });
    }
  },
}));
